import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { hivesApi } from "../services/hivesApi";
import { sitesApi } from "../services/sitesApi";
import { useSubscription } from "../context/SubscriptionContext";
import { useToast } from "../context/ToastContext";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { QrCodeLabel } from "../components/hives/QrCodeLabel";
import { UpgradeModal } from "../components/theme/UpgradeModal";
import { useFlow } from "../hooks/useFlow";
import { FLOWS } from "../services/flows";

const HIVE_TYPES = ["Langstroth", "TopBar", "Warre", "National", "WBC", "Other"];

const schema = z.object({
  site_id: z.string().min(1, "Choose a site"),
  hive_number: z.string().min(1, "Required").max(20),
  hive_type: z.enum(HIVE_TYPES),
});

const newSiteSchema = z.object({
  dock_name: z.string().min(1, "Required"),
  address: z.string().optional(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
});

/**
 * "Add new site" here depends on sitesApi.js's assumed /api/sites
 * endpoints, which don't exist in api/functions/ yet (see sitesApi.js's
 * header comment) -- this form will 404 on that call until a
 * sites.py Azure Function is added.
 */
export function AddHivePage() {
  const { atHiveLimit, refresh: refreshSubscription } = useSubscription();
  const { notify } = useToast();
  const navigate = useNavigate();
  /* #379. Leaving this page without a hive is an abandonment, and
     useFlow reports it by default -- see its note on why that is the
     direction the default has to point. */
  const flow = useFlow(FLOWS.HIVE_ADD);

  const [sites, setSites] = useState(null);
  const [creatingSite, setCreatingSite] = useState(false);
  const [createdHive, setCreatedHive] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(atHiveLimit);

  const { register, handleSubmit, setValue, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { hive_type: "Langstroth" },
  });

  const newSiteForm = useForm({ resolver: zodResolver(newSiteSchema) });

  async function loadSites() {
    const r = await sitesApi.list().catch(() => ({ sites: [] }));
    setSites(r.sites);
  }

  useEffect(() => { loadSites(); }, []);

  // Typing an address here never found the coordinates -- it was stored
  // as text, and the site sat with no location until somebody opened the
  // hive, found the site dialog and pressed the button THERE. Reported
  // after testing: "do we need a find coordinates button on the create
  // site form?" Yes; the capability existed and the form did not offer it.
  //
  // Not automatic on submit, and that is deliberate: a geocoder answers
  // confidently even when it has guessed, and a silently accepted guess
  // puts a hive in the wrong county where nobody notices until they drive
  // there. The person confirms the match.
  const [geocoding, setGeocoding] = useState(false);
  const [foundAddress, setFoundAddress] = useState(null);

  async function findCoordinates() {
    const address = (newSiteForm.getValues("address") || "").trim();
    if (!address) {
      notify("Type an address first", "warning");
      return;
    }
    setGeocoding(true);
    try {
      const r = await sitesApi.geocode(address);
      newSiteForm.setValue("latitude", String(r.latitude));
      newSiteForm.setValue("longitude", String(r.longitude));
      setFoundAddress(r.formatted_address || address);
    } catch (err) {
      // The server distinguishes "no such address" from "lookup is not
      // configured"; pass its sentence through rather than replacing it
      // with a guess about which happened.
      notify(err.apiMessage || "Couldn't find that address", "warning");
      setFoundAddress(null);
    } finally {
      setGeocoding(false);
    }
  }

  async function onCreateSite(values) {
    try {
      const payload = {
        dock_name: values.dock_name,
        address: values.address || undefined,
        latitude: values.latitude ? Number(values.latitude) : undefined,
        longitude: values.longitude ? Number(values.longitude) : undefined,
      };
      const result = await sitesApi.create(payload);
      await loadSites();
      setValue("site_id", String(result.site_id));
      setCreatingSite(false);
      notify("Site added", "success");
    } catch (err) {
      notify(err.apiMessage || "Could not add site (see AddHivePage.jsx note on sitesApi)", "danger");
    }
  }

  async function onSubmit(values) {
    try {
      const result = await hivesApi.create({ ...values, site_id: Number(values.site_id) });
      /* #379. NOT on the subscription-limit branch below: hitting the
         hive cap is a flow the app refused, not one the keeper gave up
         on, and counting it as a completion would hide the only
         abandonment on this screen worth acting on. */
      flow.finished();
      await refreshSubscription();
      setCreatedHive({ hive_id: result.hive_id, hive_number: values.hive_number });
      notify("Hive created — QR code generated", "success");
    } catch (err) {
      if (err.apiErrorCode === "subscription_limit_reached") {
        setShowUpgrade(true);
      } else {
        notify(err.apiMessage || "Could not create hive", "danger");
      }
    }
  }

  if (createdHive) {
    return (
      <Card title="Hive Created">
        <p>Hive {createdHive.hive_number} was added, with its QR code auto-generated.</p>
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          <QrCodeLabel hive={createdHive} trigger={<Button>Print QR Label</Button>} />
          <Button variant="secondary" onClick={() => navigate(`/hives/${createdHive.hive_id}`)}>View Hive</Button>
          <Button variant="ghost" onClick={() => setCreatedHive(null)}>Add Another</Button>
        </div>
      </Card>
    );
  }

  if (atHiveLimit) {
    return (
      <Card title="Hive Limit Reached">
        <p>You've reached your 3-hive limit. Upgrade to add more.</p>
        <Button onClick={() => setShowUpgrade(true)}>Upgrade</Button>
        <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} highlightFeature="Unlimited hives" />
      </Card>
    );
  }

  return (
    <Card title="Add New Hive">
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: "grid", gap: "var(--space-4)", maxWidth: 480 }}>
        <label>
          Site
          <select {...register("site_id")} className="touch-target" style={{ width: "100%" }}>
            <option value="">Select a site…</option>
            {sites?.map((s) => <option key={s.site_id} value={s.site_id}>{s.dock_name}</option>)}
          </select>
          {errors.site_id && <span style={{ color: "var(--color-danger)" }}>{errors.site_id.message}</span>}
        </label>

        <Button type="button" variant="ghost" size="sm" onClick={() => setCreatingSite((v) => !v)}>
          {creatingSite ? "Cancel new site" : "+ Add new site"}
        </Button>

        {creatingSite && (
          <div style={{ display: "grid", gap: "var(--space-3)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-3)" }}>
            <label>
              Site name
              <input {...newSiteForm.register("dock_name")} className="touch-target" style={{ width: "100%" }} />
              {newSiteForm.formState.errors.dock_name && <span style={{ color: "var(--color-danger)" }}>{newSiteForm.formState.errors.dock_name.message}</span>}
            </label>
            <label>
              Address (or leave blank and use GPS below)
              <input {...newSiteForm.register("address")} className="touch-target" style={{ width: "100%" }} />
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap", marginTop: "var(--space-1)" }}>
                <Button type="button" size="sm" variant="secondary" disabled={geocoding} onClick={findCoordinates}>
                  {geocoding ? "Looking…" : "Find coordinates"}
                </Button>
                {/* Shown so the match can be REJECTED. A geocoder is
                    confident about its guesses, and "close enough" here
                    means a hive pinned in the wrong county. */}
                {foundAddress && (
                  <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                    Found: {foundAddress}
                  </span>
                )}
              </div>
            </label>
            <div style={{ display: "flex", gap: "var(--space-3)" }}>
              <label style={{ flex: 1 }}>
                Latitude
                <input {...newSiteForm.register("latitude")} className="touch-target" style={{ width: "100%" }} placeholder="e.g. 36.7378" />
              </label>
              <label style={{ flex: 1 }}>
                Longitude
                <input {...newSiteForm.register("longitude")} className="touch-target" style={{ width: "100%" }} placeholder="e.g. -119.7871" />
              </label>
            </div>
            {/* Secondary, though it is the sub-form's own save (#327).
                This form is nested INSIDE the Add Hive form, so with the
                site fields open the user sees two full-weight primaries
                at once -- Save Site here and Create Hive below -- for two
                different jobs. Creating the hive is what the page is
                for; naming a new yard on the way is a detour. */}
            <Button type="button" size="sm" variant="secondary" onClick={newSiteForm.handleSubmit(onCreateSite)}>Save Site</Button>
          </div>
        )}

        <label>
          Hive number
          <input {...register("hive_number")} className="touch-target" style={{ width: "100%" }} />
          {errors.hive_number && <span style={{ color: "var(--color-danger)" }}>{errors.hive_number.message}</span>}
        </label>

        <label>
          Hive type
          <select {...register("hive_type")} className="touch-target" style={{ width: "100%" }}>
            {HIVE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>

        <Button type="submit" disabled={isSubmitting}>{isSubmitting ? "Creating…" : "Create Hive"}</Button>
      </form>
    </Card>
  );
}
